import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Map, Save, Download, Globe, MessageCircle, Calendar } from 'lucide-react';
import { DynamicBackground } from './DynamicBackground';
import { ChatMessages } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ConsentModal } from './ConsentModal';
import { InteractiveMap } from './InteractiveMap';
import { CalendarDrawer } from './CalendarDrawer';
import { ResumeWelcome } from './ResumeWelcome';
import { UnlockProposalModal, PaymentSuccessHandler } from './UnlockProposalModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initial greeting based on locale
const INITIAL_GREETING = {
  it: "Ciao! Sono Chiara, la tua guida per scoprire l'Umbria. Raccontami: quando vorresti venire e con chi?",
  en: "Hello! I'm Chiara, your guide to discovering Umbria. Tell me: when would you like to come and with whom?"
};

// Conversation state thresholds
// DISABLED: Automatic consent prompt was showing too early (after 3 turns)
// Now triggered only when backend sends save_draft_prompt signal
const CONSENT_PROMPT_AFTER_TURNS = 999; // Effectively disabled

// Theme signal debounce (client-side backup to server-side debounce)
const THEME_SIGNAL_DEBOUNCE_MS = 1000;
const THEME_SWITCH_MIN_INTERVAL_MS = 15000; // Minimum 15 seconds between theme switches

// P1 FIX: Max messages to render to prevent Safari memory issues
const MAX_VISIBLE_MESSAGES = 50;

// Simple client-side theme detection based on keywords
const detectThemeFromText = (text) => {
  if (!text) return null;
  const textLower = text.toLowerCase();
  
  const themeKeywords = {
    biking: ['bici', 'bike', 'cycling', 'ciclismo', 'pedalare', 'e-bike', 'gravel', 'mtb'],
    hiking: ['trekking', 'camminata', 'sentiero', 'escursione', 'hiking', 'trail', 'montagna'],
    wine_food: ['vino', 'wine', 'cibo', 'food', 'degustazione', 'tasting', 'cucina', 'cantina', 'olio'],
    truffle: ['tartufo', 'truffle', 'norcia'],
    borghi: ['borgo', 'borghi', 'village', 'medieval', 'medievale', 'centro storico'],
    assisi_spiritual: ['assisi', 'san francesco', 'spirituale', 'spiritual', 'basilica'],
    lake: ['lago', 'lake', 'trasimeno', 'nuoto', 'swim', 'spiaggia', 'barca', 'boat'],
    artisan: ['artigiano', 'artisan', 'ceramica', 'pottery', 'workshop', 'deruta'],
    family: ['bambini', 'children', 'famiglia', 'family', 'kids'],
    night: ['notte', 'night', 'sera', 'evening', 'cena', 'dinner', 'tramonto', 'sunset'],
  };
  
  for (const [theme, keywords] of Object.entries(themeKeywords)) {
    for (const keyword of keywords) {
      if (textLower.includes(keyword)) {
        return theme;
      }
    }
  }
  return null;
};

// Explicit confirmations that bypass debounce
const isExplicitConfirmation = (text) => {
  const textLower = text.toLowerCase();
  const confirmPhrases = [
    'preferisco', 'voglio', 'vorrei', 'mi piace', 'scegliamo',
    'andiamo con', 'facciamo', "let's do", 'i want', 'i prefer'
  ];
  return confirmPhrases.some(phrase => textLower.includes(phrase));
};

export const ChatInterface = () => {
  // Core state
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [locale, setLocale] = useState('it');
  
  // Theme state (stable, from ThemeSignalExtractor)
  const [stableThemeKey, setStableThemeKey] = useState('umbria_general');
  const [conversationState, setConversationState] = useState('DISCOVERY');
  
  // Theme signal request state
  const themeSignalTimerRef = useRef(null);
  const lastThemeSignalRef = useRef(0);
  const lastThemeSwitchRef = useRef(0);  // Track last switch time for debounce
  
  // P0 FIX: Prevent duplicate thread creation from StrictMode/race conditions
  const initializingRef = useRef(false);
  const threadIdRef = useRef(null); // Sync ref for race condition checks
  
  // Resume/Deep Link state
  const [searchParams] = useSearchParams();
  const { token: urlToken } = useParams();
  const resumeToken = urlToken || searchParams.get('resume');
  const [showResumeWelcome, setShowResumeWelcome] = useState(!!resumeToken);
  const [isResuming, setIsResuming] = useState(!!resumeToken);
  
  // Modals and panels
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showMapPanel, setShowMapPanel] = useState(false);
  const [showCalendarDrawer, setShowCalendarDrawer] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [consentPrompted, setConsentPrompted] = useState(false);
  
  // Payment/unlock state
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [commercialState, setCommercialState] = useState('EXPLORE_FREE');
  
  // Check for payment success return from Stripe
  const paymentSessionId = searchParams.get('session_id');
  const paymentStatus = searchParams.get('payment');
  
  // Briefing data for map
  const [briefingData, setBriefingData] = useState(null);
  
  // Map command from Chiara (new map experience)
  const [mapCommand, setMapCommand] = useState(null);
  
  // Calendar/date state (persisted across panel open/close)
  const [selectedDates, setSelectedDates] = useState(null);
  
  // Map context state (from conversation)
  const [mapContext, setMapContext] = useState({
    userBase: null,
    userArea: null,
    highlightArea: null,
    suggestedLocations: [],
    accommodationState: null,  // "base_known" | "area_preferred" | "base_undecided"
    suggestedBases: [],        // When undecided: [{area, rationale, towns, sample_accommodation}]
    pulseLocations: [],        // Locations mentioned (pulse marker)
    fixedLocations: []         // Locations confirmed by user (fixed marker)
  });
  
  // Known Italian locations for detection
  const KNOWN_LOCATIONS = [
    'assisi', 'spello', 'bevagna', 'montefalco', 'foligno', 'trevi', 'spoleto',
    'perugia', 'orvieto', 'todi', 'norcia', 'cascia', 'gubbio', 'castelluccio',
    'trasimeno', 'castiglione del lago', 'passignano', 'città della pieve',
    'deruta', 'narni', 'terni', 'città di castello', 'marmore', 'cascata'
  ];
  
  // Detect locations mentioned in assistant message
  const detectMentionedLocations = useCallback((text) => {
    const textLower = text.toLowerCase();
    const mentioned = [];
    for (const loc of KNOWN_LOCATIONS) {
      if (textLower.includes(loc)) {
        mentioned.push(loc);
      }
    }
    return mentioned;
  }, []);

  // P0 FIX: Persist thread ID in localStorage to prevent losing conversation on page reload
  const THREAD_STORAGE_KEY = 'feel_umbria_current_thread';
  
  // P1 FIX: Cleanup timers on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (themeSignalTimerRef.current) {
        clearTimeout(themeSignalTimerRef.current);
      }
    };
  }, []);

  // Initialize thread on mount - check localStorage first
  useEffect(() => {
    initializeThread();
  }, []);

  // Save threadId to localStorage and ref whenever it changes
  useEffect(() => {
    if (threadId) {
      localStorage.setItem(THREAD_STORAGE_KEY, threadId);
      threadIdRef.current = threadId;
    }
  }, [threadId]);

  // Check if we should prompt for consent
  useEffect(() => {
    const userTurns = messages.filter(m => m.role === 'user').length;
    if (userTurns >= CONSENT_PROMPT_AFTER_TURNS && !hasConsent && !consentPrompted) {
      // Wait a bit before showing modal
      const timer = setTimeout(() => {
        setShowConsentModal(true);
        setConsentPrompted(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [messages, hasConsent, consentPrompted]);

  // Show map when conversation advances to DRAFT
  useEffect(() => {
    if (conversationState === 'DRAFT' || conversationState === 'VERIFY_PENDING') {
      // Auto-show map on first DRAFT state
      if (!showMapPanel && messages.length > 6) {
        setShowMapPanel(true);
      }
    }
  }, [conversationState, messages.length, showMapPanel]);

  const initializeThread = async () => {
    // P0 FIX: ALWAYS check localStorage first - this is the source of truth
    const existingThreadId = localStorage.getItem(THREAD_STORAGE_KEY);
    
    // If thread exists, just load it
    if (existingThreadId) {
      console.log('[initializeThread] Found existing thread:', existingThreadId.slice(0, 8));
      threadIdRef.current = existingThreadId;
      setThreadId(existingThreadId);
      
      try {
        const threadResponse = await axios.get(`${API}/threads/${existingThreadId}`);
        if (threadResponse.data && threadResponse.data.id) {
          setLocale(threadResponse.data.locale || 'it');
          setStableThemeKey(threadResponse.data.theme_key || 'umbria_general');
          setConversationState(threadResponse.data.state || 'DISCOVERY');
          
          const messagesResponse = await axios.get(`${API}/threads/${existingThreadId}/messages`);
          if (messagesResponse.data && messagesResponse.data.length > 0) {
            setMessages(messagesResponse.data);
            
            try {
              const briefingResponse = await axios.get(`${API}/threads/${existingThreadId}/briefing`);
              if (briefingResponse.data) {
                setBriefingData(briefingResponse.data);
                setCommercialState(briefingResponse.data.commercial_state);
                setIsUnlocked(briefingResponse.data.commercial_state === 'UNLOCKED_PROJECT');
              }
            } catch (e) {
              console.log('No briefing data yet');
            }
            
            fetchThemeSignal(existingThreadId);
          } else {
            // Thread exists but no messages - add greeting
            setMessages([{
              id: 'greeting',
              role: 'assistant',
              content: INITIAL_GREETING[threadResponse.data.locale || 'it']
            }]);
          }
          return;
        }
      } catch (error) {
        // Thread not found on server - clear and create new
        console.log('Saved thread not found on server, clearing');
        localStorage.removeItem(THREAD_STORAGE_KEY);
        threadIdRef.current = null;
      }
    }
    
    // P0 FIX: Use lock to prevent race conditions between StrictMode mounts
    const INIT_LOCK_KEY = 'feel_umbria_init_lock';
    const lockTimestamp = localStorage.getItem(INIT_LOCK_KEY);
    const now = Date.now();
    
    if (lockTimestamp && (now - parseInt(lockTimestamp)) < 3000) {
      console.log('[initializeThread] Creation locked, waiting for other call...');
      // Wait and check if thread was created
      await new Promise(resolve => setTimeout(resolve, 1500));
      const createdThread = localStorage.getItem(THREAD_STORAGE_KEY);
      if (createdThread) {
        console.log('[initializeThread] Thread created by other call:', createdThread.slice(0, 8));
        threadIdRef.current = createdThread;
        setThreadId(createdThread);
        setMessages([{
          id: 'greeting',
          role: 'assistant',
          content: INITIAL_GREETING['it']
        }]);
        return;
      }
    }
    
    // Set lock BEFORE async operation
    localStorage.setItem(INIT_LOCK_KEY, now.toString());
    
    // If we have a resume token, show the welcome screen first
    if (resumeToken) {
      setShowResumeWelcome(true);
      setIsResuming(true);
      localStorage.removeItem(INIT_LOCK_KEY);
      return;
    }
    
    try {
      // Final check before creating (another call might have just finished)
      const finalCheck = localStorage.getItem(THREAD_STORAGE_KEY);
      if (finalCheck) {
        console.log('[initializeThread] Thread appeared during lock, using it:', finalCheck.slice(0, 8));
        threadIdRef.current = finalCheck;
        setThreadId(finalCheck);
        setMessages([{
          id: 'greeting',
          role: 'assistant',
          content: INITIAL_GREETING['it']
        }]);
        localStorage.removeItem(INIT_LOCK_KEY);
        return;
      }
      
      // Create new thread
      console.log('[initializeThread] Creating new thread');
      const response = await axios.post(`${API}/threads`, { locale: 'it' });
      const thread = response.data;
      
      // Immediately save to localStorage BEFORE setting state
      localStorage.setItem(THREAD_STORAGE_KEY, thread.id);
      threadIdRef.current = thread.id;
      setThreadId(thread.id);
      setLocale(thread.locale);
      
      setMessages([{
        id: 'greeting',
        role: 'assistant',
        content: INITIAL_GREETING[thread.locale]
      }]);
    } catch (error) {
      console.error('Thread initialization error:', error);
      setMessages([{
        id: 'greeting',
        role: 'assistant',
        content: INITIAL_GREETING.it
      }]);
    } finally {
      localStorage.removeItem(INIT_LOCK_KEY);
    }
  };

  // Handler for session restored from deep link
  const handleSessionLoaded = (sessionData) => {
    const { thread, briefing, recent_messages, draft_summary, updates } = sessionData;
    
    // Hydrate state from restored session
    setThreadId(thread.id);
    setLocale(thread.locale || 'it');
    setStableThemeKey(thread.theme_key || 'umbria_general');
    setConversationState(thread.state || 'DISCOVERY');
    setHasConsent(true);
    
    // Load messages
    if (recent_messages && recent_messages.length > 0) {
      setMessages(recent_messages);
    } else {
      // Fallback: fetch from API
      axios.get(`${API}/threads/${thread.id}/messages`)
        .then(res => setMessages(res.data))
        .catch(console.error);
    }
    
    // Set briefing data for map
    if (briefing) {
      setBriefingData(briefing);
      
      // Restore map command if available
      if (briefing.map_command) {
        setMapCommand(briefing.map_command);
      }
    }
    
    // Add welcome back message if we have updates
    if (updates && updates.length > 0) {
      const updateMessage = {
        id: `update-${Date.now()}`,
        role: 'assistant',
        content: locale === 'it' 
          ? `Bentornato! Ecco le novità:\n${updates.map(u => `• ${u}`).join('\n')}\n\nRiprendiamo da dove eravamo rimasti?`
          : `Welcome back! Here are the updates:\n${updates.map(u => `• ${u}`).join('\n')}\n\nShall we pick up where we left off?`
      };
      setMessages(prev => [...prev, updateMessage]);
    }
    
    // Fetch current theme signal
    fetchThemeSignal(thread.id);
    
    // Close welcome screen
    setShowResumeWelcome(false);
    setIsResuming(false);
  };

  // Handler for resume errors
  const handleResumeError = (error) => {
    console.error('Resume error:', error);
    // Stay on error screen - ResumeWelcome handles it
  };

  /**
   * Fetch theme signal from backend with debounce
   * This uses the ThemeSignalExtractor for stable, flicker-free theme detection
   */
  const fetchThemeSignal = useCallback(async (tid = threadId) => {
    if (!tid) {
      console.log('[fetchThemeSignal] No thread ID');
      return;
    }
    
    // Client-side debounce as backup
    const now = Date.now();
    if (now - lastThemeSignalRef.current < THEME_SIGNAL_DEBOUNCE_MS) {
      console.log('[fetchThemeSignal] Debounced');
      return;
    }
    lastThemeSignalRef.current = now;
    
    try {
      console.log(`[fetchThemeSignal] Calling API for thread ${tid}`);
      const response = await axios.post(`${API}/theme-signal`, {
        thread_id: tid,
        message_limit: 4
      });
      
      const { stable_theme_key, switch_allowed, switch_reason } = response.data;
      console.log(`[fetchThemeSignal] Response: stable=${stable_theme_key}, allowed=${switch_allowed}, reason=${switch_reason}`);
      
      // Update theme state - use functional update to avoid stale closure
      if (switch_allowed) {
        console.log(`[fetchThemeSignal] Switch allowed, updating to ${stable_theme_key}`);
        setStableThemeKey(currentTheme => {
          if (stable_theme_key !== currentTheme) {
            console.log(`Theme switch: ${currentTheme} -> ${stable_theme_key} (${switch_reason})`);
            return stable_theme_key;
          }
          console.log(`[fetchThemeSignal] Same theme, no update needed`);
          return currentTheme;
        });
      } else {
        console.log(`[fetchThemeSignal] Switch not allowed`);
      }
    } catch (error) {
      console.error('Theme signal fetch error:', error);
      // Don't change theme on error
    }
  }, [threadId]);

  // P1 FIX: Memoize sendMessage to prevent unnecessary re-renders
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isLoading) return;
    
    // Client-side theme detection with debounce
    const detectedTheme = detectThemeFromText(content);
    const now = Date.now();
    const timeSinceLastSwitch = now - lastThemeSwitchRef.current;
    const isExplicit = isExplicitConfirmation(content);
    
    // Allow theme switch if:
    // 1. It's an explicit confirmation (bypass debounce)
    // 2. OR sufficient time has passed since last switch
    // 3. AND theme is different from current
    if (detectedTheme && detectedTheme !== stableThemeKey) {
      if (isExplicit || timeSinceLastSwitch >= THEME_SWITCH_MIN_INTERVAL_MS) {
        setStableThemeKey(detectedTheme);
        lastThemeSwitchRef.current = now;
      }
    }
    
    // Add user message optimistically
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // P0 FIX: Use ref for immediate access, check localStorage as fallback
      let currentThreadId = threadIdRef.current || threadId || localStorage.getItem(THREAD_STORAGE_KEY);
      
      if (!currentThreadId) {
        // P0 FIX: Wait for initialization if in progress
        if (initializingRef.current) {
          console.log('[sendMessage] Waiting for initialization...');
          // Wait a bit and retry
          await new Promise(resolve => setTimeout(resolve, 500));
          currentThreadId = threadIdRef.current || localStorage.getItem(THREAD_STORAGE_KEY);
        }
        
        // Still no thread? Create one
        if (!currentThreadId) {
          console.log('[sendMessage] Creating new thread');
          const threadRes = await axios.post(`${API}/threads`, { locale });
          currentThreadId = threadRes.data.id;
          localStorage.setItem(THREAD_STORAGE_KEY, currentThreadId);
          threadIdRef.current = currentThreadId;
          setThreadId(currentThreadId);
        }
      }

      // Send message to API using the correct thread ID
      const response = await axios.post(`${API}/threads/${currentThreadId}/messages`, {
        content
      });

      const { message: assistantMsg, thread, briefing } = response.data;
      
      // Update state from response
      if (thread) {
        setLocale(thread.locale || locale);
        setConversationState(thread.state || conversationState);
      }
      
      if (briefing) {
        setBriefingData(briefing);
        // Update conversation state based on briefing
        if (briefing.itinerary_draft && briefing.itinerary_draft.length > 0) {
          setConversationState('DRAFT');
        }
        
        // ============== MAP EXPERIENCE CORE ==============
        // If backend sent a map_command, use it to control the map
        if (briefing.map_command) {
          setMapCommand(briefing.map_command);
          // Auto-show map when Chiara sends a map command
          setShowMapPanel(true);
        }
        
        // Update commercial state from briefing
        if (briefing.commercial_state) {
          setCommercialState(briefing.commercial_state);
          // Check if already unlocked
          if (briefing.commercial_state === 'UNLOCKED_PROJECT') {
            setIsUnlocked(true);
          }
        }
        
        // Check if Chiara suggests unlocking (show_unlock_cta)
        if (briefing.show_unlock_cta && !isUnlocked) {
          setShowUnlockModal(true);
        }
        
        // Update map context from briefing
        const intentSignals = briefing.intent_signals || {};
        
        // Extract suggested locations from experiences in the response
        const suggestedExps = briefing.suggested_experiences || [];
        const locationsFromExps = suggestedExps
          .filter(exp => exp.suggested_towns || exp.location || exp.macro_area)
          .map(exp => ({
            title: exp.title || exp.experience_id,
            name: exp.title,
            area: exp.macro_area,
            towns: exp.suggested_towns,
            category: exp.category
          }));
        
        setMapContext(prev => ({
          ...prev,
          userBase: briefing.base_location || intentSignals.base_area || intentSignals.confirmed_base || prev.userBase,
          userArea: intentSignals.macro_area || intentSignals.preferred_area || prev.userArea,
          accommodationState: intentSignals.accommodation_state || prev.accommodationState,
          suggestedBases: intentSignals.suggested_bases || prev.suggestedBases,
          suggestedLocations: locationsFromExps.length > 0 ? locationsFromExps : prev.suggestedLocations
        }));
      }

      // Detect locations mentioned in assistant message for pulse markers
      const mentionedLocs = detectMentionedLocations(assistantMsg.content);
      if (mentionedLocs.length > 0) {
        setMapContext(prev => ({
          ...prev,
          pulseLocations: mentionedLocs
        }));
        
        // If map is visible and locations were mentioned, update mapCommand
        if (showMapPanel && mentionedLocs.length > 0) {
          setMapCommand({
            mode: 'suggestion',
            pulse: mentionedLocs[0], // Pulse on first mentioned location
            mentions: mentionedLocs
          });
        }
      }

      // Add assistant message
      setMessages(prev => [...prev, {
        id: assistantMsg.id,
        role: 'assistant',
        content: assistantMsg.content
      }]);
      
      // Also check assistant response for theme hints
      const assistantTheme = detectThemeFromText(assistantMsg.content);
      if (assistantTheme && !detectedTheme) {
        setStableThemeKey(assistantTheme);
      }
      
      // Backup: Use backend theme signal for more sophisticated detection
      if (themeSignalTimerRef.current) {
        clearTimeout(themeSignalTimerRef.current);
      }
      const threadIdForSignal = currentThreadId;
      themeSignalTimerRef.current = setTimeout(() => {
        fetchThemeSignal(threadIdForSignal);
      }, 1000);

    } catch (error) {
      console.error('Send message error:', error);
      // Add error message
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: locale === 'it' 
          ? 'Mi scuso, ho avuto un problema tecnico. Puoi ripetere?'
          : 'I apologize, I had a technical issue. Can you repeat?'
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, threadId, locale, stableThemeKey, fetchThemeSignal]);

  const handleConsent = async (consentData) => {
    if (!threadId) return;
    
    try {
      // Map camelCase to snake_case for API
      const apiData = {
        email: consentData.email,
        privacy_accepted: consentData.privacyAccepted,
        updates_opt_in: consentData.updatesOptIn || false
      };
      
      await axios.post(`${API}/threads/${threadId}/consent`, apiData);
      setHasConsent(true);
      
      // Add confirmation message
      setMessages(prev => [...prev, {
        id: `consent-${Date.now()}`,
        role: 'system',
        content: locale === 'it'
          ? 'Bozza salvata! Ti invieremo un riepilogo via email.'
          : 'Draft saved! We\'ll send you a summary via email.'
      }]);
    } catch (error) {
      console.error('Consent save error:', error);
      throw error;
    }
  };

  const handleExport = async () => {
    if (!threadId) return;
    
    try {
      const response = await axios.post(`${API}/threads/${threadId}/export?format=xlsx`);
      const { download_url } = response.data;
      
      // Download file
      window.open(`${process.env.REACT_APP_BACKEND_URL}${download_url}`, '_blank');
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const toggleLocale = () => {
    const newLocale = locale === 'it' ? 'en' : 'it';
    setLocale(newLocale);
  };

  // Handle selection from map (bidirectional: Map → Chiara)
  const handleMapSelection = async (selection) => {
    let userMessage = '';
    
    if (selection.type === 'scenario') {
      // User clicked a scenario card (e.g., "Wine Country Retreat")
      userMessage = locale === 'it'
        ? `Mi interessa la direzione "${selection.scenarioName}" nella zona ${selection.scenarioArea}`
        : `I'm interested in the "${selection.scenarioName}" direction in the ${selection.scenarioArea} area`;
    } else if (selection.type === 'area') {
      // User clicked an area in exploration mode
      userMessage = locale === 'it'
        ? `Vorrei esplorare la zona ${selection.areaName}`
        : `I'd like to explore the ${selection.areaName} area`;
    } else if (selection.type === 'stop') {
      // User clicked a stop/marker
      userMessage = locale === 'it'
        ? `Dimmi di più su ${selection.stopName}`
        : `Tell me more about ${selection.stopName}`;
    }
    
    if (userMessage) {
      // Close map panel and send message
      setShowMapPanel(false);
      await sendMessage(userMessage);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = async (paymentData) => {
    setIsUnlocked(true);
    setCommercialState('UNLOCKED_PROJECT');
    
    // Reload briefing to get updated data
    if (threadId) {
      try {
        const res = await axios.get(`${API}/threads/${threadId}`);
        if (res.data.briefing) {
          setBriefingData(res.data.briefing);
        }
      } catch (err) {
        console.error('Failed to reload briefing:', err);
      }
    }
    
    // Clear URL params
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handlePaymentError = (errorMsg) => {
    console.error('Payment error:', errorMsg);
    // Clear URL params
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Check if we should show unlock modal based on commercial state
  const shouldShowUnlockOption = commercialState === 'READY_TO_UNLOCK' && !isUnlocked;

  // Extract locations from briefing for map
  const mapLocations = briefingData?.itinerary_draft?.map(day => ({
    name: day.location || day.title,
    description: day.experience
  })) || [];

  // P1 FIX: Memoize visible messages to prevent Safari memory issues
  const visibleMessages = useMemo(() => {
    if (messages.length > MAX_VISIBLE_MESSAGES) {
      return messages.slice(-MAX_VISIBLE_MESSAGES);
    }
    return messages;
  }, [messages]);

  // P1 FIX: Memoize contextual action handler
  const handleContextualAction = useCallback((action) => {
    if (action.type === 'calendar') {
      setShowCalendarDrawer(true);
    } else if (action.type === 'map') {
      if (action.area) {
        setMapContext(prev => ({
          ...prev,
          highlightArea: action.area
        }));
      }
      setShowMapPanel(true);
    }
  }, []);

  // Handle starting a new conversation
  const handleNewConversation = useCallback(async () => {
    if (window.confirm('Vuoi iniziare una nuova conversazione? La conversazione attuale non verrà cancellata se hai salvato la bozza.')) {
      // Clear localStorage and ref
      localStorage.removeItem(THREAD_STORAGE_KEY);
      threadIdRef.current = null;
      
      // Reset state
      setMessages([]);
      setThreadId(null);
      setBriefingData(null);
      setCommercialState(null);
      setIsUnlocked(false);
      setConversationState('DISCOVERY');
      setHasConsent(false);
      setConsentPrompted(false);
      setShowMapPanel(false);
      setMapContext({
        userBase: null,
        userArea: null,
        highlightArea: null,
        suggestedLocations: [],
        accommodationState: null,
        suggestedBases: []
      });
      
      // Create new thread
      try {
        const response = await axios.post(`${API}/threads`, { locale: 'it' });
        const thread = response.data;
        // P0 FIX: Save immediately to localStorage and ref
        localStorage.setItem(THREAD_STORAGE_KEY, thread.id);
        threadIdRef.current = thread.id;
        setThreadId(thread.id);
        setLocale(thread.locale);
        setMessages([{
          id: 'greeting',
          role: 'assistant',
          content: INITIAL_GREETING[thread.locale]
        }]);
      } catch (error) {
        console.error('Error creating new thread:', error);
      }
    }
  }, []);

  // Show payment success handler if returning from Stripe
  if (paymentStatus === 'success' && paymentSessionId) {
    return (
      <PaymentSuccessHandler
        sessionId={paymentSessionId}
        locale={locale}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />
    );
  }

  // Show resume welcome screen if resuming
  if (showResumeWelcome && resumeToken) {
    return (
      <ResumeWelcome
        token={resumeToken}
        locale={locale}
        onSessionLoaded={handleSessionLoaded}
        onError={handleResumeError}
      />
    );
  }

  return (
    <DynamicBackground themeKey={stableThemeKey} phase={conversationState}>
      <div className="min-h-screen flex flex-col" data-testid="chat-interface">
        {/* Header */}
        <header className="flex items-center justify-between p-4 md:p-6">
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-xl md:text-2xl text-white text-shadow-subtle font-semibold tracking-tight">
              Feel Umbria
            </h1>
            {conversationState !== 'DISCOVERY' && (
              <span className="text-xs px-2 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm">
                {conversationState}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Locale toggle */}
            <button
              onClick={toggleLocale}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm hover:bg-white/30 transition-colors"
              data-testid="locale-toggle"
            >
              <Globe size={14} />
              {locale.toUpperCase()}
            </button>
            
            {/* Calendar toggle */}
            <button
              onClick={() => setShowCalendarDrawer(!showCalendarDrawer)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                showCalendarDrawer 
                  ? 'bg-umbria-olive text-white' 
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
              }`}
              data-testid="calendar-toggle"
            >
              <Calendar size={14} />
              {selectedDates?.formatted ? selectedDates.formatted.split(' - ')[0] : 'Date'}
            </button>
            
            {/* Map toggle */}
            <button
              onClick={() => setShowMapPanel(!showMapPanel)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                showMapPanel 
                  ? 'bg-umbria-olive text-white' 
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
              }`}
              data-testid="map-toggle"
            >
              <Map size={14} />
              {mapContext.userBase || 'Mappa'}
            </button>
            
            {/* New conversation button */}
            <button
              onClick={handleNewConversation}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm hover:bg-white/30 transition-colors"
              data-testid="new-conversation-btn"
              title="Inizia una nuova conversazione"
            >
              <MessageCircle size={14} />
              Nuova
            </button>
            
            {/* Save draft button */}
            {!hasConsent && messages.length > 2 && (
              <button
                onClick={() => setShowConsentModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm hover:bg-white/30 transition-colors"
                data-testid="save-draft-btn"
              >
                <Save size={14} />
                Salva
              </button>
            )}
            
            {/* Export button - show when has consent and in DRAFT+ */}
            {hasConsent && conversationState !== 'DISCOVERY' && (
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm hover:bg-white/30 transition-colors"
                data-testid="export-btn"
              >
                <Download size={14} />
                Export
              </button>
            )}
          </div>
        </header>

        {/* Main chat area */}
        <main className="flex-1 flex items-center justify-center p-4">
          <div 
            className={`w-full max-w-2xl h-[70vh] md:h-[75vh] glass-container rounded-3xl flex flex-col overflow-hidden transition-all duration-500 ${
              showMapPanel ? 'md:mr-[420px] lg:mr-[520px]' : ''
            }`}
          >
            {/* Chat header */}
            <div className="px-5 py-4 border-b border-umbria-stone/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-umbria-olive flex items-center justify-center">
                  <MessageCircle className="text-white" size={20} />
                </div>
                <div>
                  <h2 className="font-medium text-umbria-charcoal">Chiara</h2>
                  <p className="text-xs text-gray-500">
                    {locale === 'it' ? 'La tua guida in Umbria' : 'Your Umbria guide'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Messages - P1 FIX: Using memoized visible messages */}
            <ChatMessages 
              messages={visibleMessages} 
              isLoading={isLoading}
              onContextualAction={handleContextualAction}
            />
            
            {/* Input */}
            <ChatInput 
              onSend={sendMessage} 
              disabled={isLoading}
              placeholder={locale === 'it' ? 'Scrivi un messaggio...' : 'Type a message...'}
            />
          </div>
        </main>

        {/* Footer */}
        <footer className="p-4 text-center space-y-2">
          <p className="text-xs text-white/60">
            {locale === 'it' 
              ? 'Feel Umbria - Esperienze autentiche in Umbria'
              : 'Feel Umbria - Authentic experiences in Umbria'
            }
          </p>
          {/* Provider Portal CTA */}
          <a 
            href="/provider"
            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors group"
            data-testid="provider-portal-link"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-umbria-olive/60 group-hover:bg-umbria-olive transition-colors" />
            {locale === 'it' 
              ? 'Sei un operatore umbro? Registra la tua attività'
              : 'Are you an Umbrian operator? Register your business'
            }
          </a>
        </footer>

        {/* Consent Modal */}
        <ConsentModal
          isOpen={showConsentModal}
          onClose={() => setShowConsentModal(false)}
          onSubmit={handleConsent}
          locale={locale}
        />

        {/* Map Panel - Interactive Map with GeoJSON boundaries */}
        <InteractiveMap
          isOpen={showMapPanel}
          onClose={() => setShowMapPanel(false)}
          mapCommand={mapCommand}
          onLocationSelect={handleMapSelection}
        />
        
        {/* Calendar Drawer */}
        <CalendarDrawer
          isOpen={showCalendarDrawer}
          onClose={() => setShowCalendarDrawer(false)}
          selectedDates={selectedDates}
          onDateSelect={(dates) => {
            setSelectedDates(dates);
            // Add a system message about the date selection
            if (dates.formatted) {
              const dateMessage = {
                id: `date-${Date.now()}`,
                role: 'user',
                content: locale === 'it' 
                  ? `Ho selezionato: ${dates.formatted}`
                  : `I selected: ${dates.formatted}`
              };
              // Send this as an actual message to continue the conversation
              sendMessage(dateMessage.content);
            }
          }}
          threadContext={{
            locale,
            hasBase: !!mapContext.userBase
          }}
        />
        
        {/* Unlock Proposal Modal */}
        <UnlockProposalModal
          isOpen={showUnlockModal}
          onClose={() => setShowUnlockModal(false)}
          threadId={threadId}
          locale={locale}
          onUnlockSuccess={() => {
            setIsUnlocked(true);
            setCommercialState('UNLOCKED_PROJECT');
          }}
        />
      </div>
    </DynamicBackground>
  );
};

export default ChatInterface;
